import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import ParentFAQ from '../components/ParentFAQ';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { 
  Heart, MessageCircle, Pin, Send, Plus, Users, Trophy, Star, 
  Calendar, Gift, Image, X, ThumbsUp, PartyPopper, HandHeart, 
  Flag, Lock, Clock, Video, Play, MoreHorizontal, Share2, Bookmark,
  ChevronLeft, ChevronRight, Camera, Smile, CheckCircle2
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REACTION_ICONS = {
  like: { icon: Heart, label: 'Like', color: 'text-red-500', activeBg: 'bg-red-50' },
  celebrate: { icon: PartyPopper, label: 'Celebrate', color: 'text-yellow-500', activeBg: 'bg-yellow-50' },
  support: { icon: HandHeart, label: 'Support', color: 'text-pink-500', activeBg: 'bg-pink-50' },
  team_spirit: { icon: Flag, label: 'Team Spirit', color: 'text-green-500', activeBg: 'bg-green-50' }
};

const ROLE_COLORS = {
  super_admin: 'ring-red',
  league_admin: 'ring-red',
  coach: 'ring-navy',
  team_staff: 'ring-navy',
  parent: 'ring-gold',
  volunteer: 'ring-green-500'
};

const ROLE_LABELS = {
  super_admin: 'Admin',
  league_admin: 'Admin',
  coach: 'Coach',
  team_staff: 'Staff',
  parent: '',
  volunteer: 'Volunteer'
};

const CommunityContent = ({ embedded = false }) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const faqAutoOpen = !embedded && searchParams.get('faq') === 'open';
  const [posts, setPosts] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', body: '', post_type: 'general_update' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [activeDivision, setActiveDivision] = useState('all');
  const [pendingImages, setPendingImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [communityPermissions, setCommunityPermissions] = useState(null);
  const [playingVideos, setPlayingVideos] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [inlineComments, setInlineComments] = useState({});
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null); // 'composer' or post_id for inline
  const [followedDivisions, setFollowedDivisions] = useState([]);
  const [followLoading, setFollowLoading] = useState({});
  const divScrollRef = useRef(null);
  const composerRef = useRef(null);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'league_admin';
  const isCoach = user?.role === 'coach' || user?.role === 'team_staff';

  useEffect(() => {
    fetchDivisions();
    fetchFollowing();
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [activeDivision]);

  const fetchDivisions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/divisions`, { withCredentials: true });
      setDivisions(res.data);
    } catch (error) {
      console.error('Failed to fetch divisions:', error);
    }
  };

  const fetchFollowing = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/user/following`, { withCredentials: true });
      setFollowedDivisions(res.data.following || []);
    } catch (error) {
      console.error('Failed to fetch following:', error);
    }
  };

  const toggleFollow = async (divisionId) => {
    setFollowLoading(prev => ({ ...prev, [divisionId]: true }));
    try {
      const isFollowing = followedDivisions.includes(divisionId);
      if (isFollowing) {
        await axios.delete(`${API_URL}/api/divisions/${divisionId}/follow`, { withCredentials: true });
        setFollowedDivisions(prev => prev.filter(id => id !== divisionId));
      } else {
        await axios.post(`${API_URL}/api/divisions/${divisionId}/follow`, {}, { withCredentials: true });
        setFollowedDivisions(prev => [...prev, divisionId]);
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    } finally {
      setFollowLoading(prev => ({ ...prev, [divisionId]: false }));
    }
  };

  const fetchFeed = async () => {
    try {
      let url = `${API_URL}/api/community/feed?limit=50`;
      if (activeDivision !== 'all') {
        url += `&feed_type=team`;
      }
      const res = await axios.get(url, { withCredentials: true });
      let feedPosts = res.data;
      if (activeDivision !== 'all') {
        feedPosts = feedPosts.filter(p => p.division_id === activeDivision || !p.division_id);
      }
      setPosts(feedPosts);
    } catch (error) {
      console.error('Failed to fetch feed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API_URL}/api/admin/community-permissions`, { withCredentials: true })
        .then(res => setCommunityPermissions(res.data))
        .catch(() => {});
    }
  }, [isAdmin]);

  const generateVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.onloadeddata = () => { video.currentTime = Math.min(1, video.duration / 4); };
      video.onseeked = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        URL.revokeObjectURL(video.src);
        resolve(dataUrl);
      };
      video.onerror = () => { URL.revokeObjectURL(video.src); resolve(null); };
      video.src = URL.createObjectURL(file);
    });
  };

  const uploadThumbnail = async (thumbnailBase64) => {
    try {
      const res = await axios.post(`${API_URL}/api/community/images/upload`, {
        file_data: thumbnailBase64, filename: 'thumbnail.jpg', content_type: 'image/jpeg'
      }, { withCredentials: true });
      return res.data.image_id;
    } catch { return null; }
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (pendingImages.length + files.length > 3) { alert('Maximum 3 media items per post'); return; }
    const imageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const videoTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    const allowedTypes = [...imageTypes, ...videoTypes];
    for (const file of files) {
      const isVideo = videoTypes.includes(file.type);
      const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) { alert(`${file.name} is too large. Max ${isVideo ? '50MB' : '5MB'}.`); continue; }
      if (!allowedTypes.includes(file.type)) { alert(`${file.name}: unsupported format.`); continue; }
      setUploadingImage(true);
      try {
        let thumbnailId = null, thumbnailPreview = null;
        if (isVideo) {
          const thumbBase64 = await generateVideoThumbnail(file);
          if (thumbBase64) { thumbnailPreview = thumbBase64; thumbnailId = await uploadThumbnail(thumbBase64); }
        }
        if (isVideo && file.size > 4 * 1024 * 1024) {
          const chunkSize = 3 * 1024 * 1024, totalChunks = Math.ceil(file.size / chunkSize);
          let uploadId = null;
          for (let i = 0; i < totalChunks; i++) {
            const chunk = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
            const chunkBase64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(chunk); });
            const resp = await axios.post(`${API_URL}/api/community/media/upload-chunk`, {
              upload_id: uploadId, chunk_data: chunkBase64, chunk_index: i, total_chunks: totalChunks,
              filename: file.name, content_type: file.type, thumbnail_id: thumbnailId
            }, { withCredentials: true });
            if (!uploadId) uploadId = resp.data.upload_id;
            if (resp.data.status === 'complete') {
              setPendingImages(prev => [...prev, { image_id: resp.data.media.image_id, url: resp.data.media.url, filename: file.name, media_type: 'video', thumbnailPreview }]);
            }
          }
        } else {
          const base64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
          const resp = await axios.post(`${API_URL}/api/community/images/upload`, {
            file_data: base64, filename: file.name, content_type: file.type, thumbnail_id: thumbnailId
          }, { withCredentials: true });
          setPendingImages(prev => [...prev, { image_id: resp.data.image_id, url: resp.data.url || base64, filename: file.name, media_type: resp.data.media_type || (isVideo ? 'video' : 'image'), thumbnailPreview: isVideo ? thumbnailPreview : null }]);
        }
      } catch (error) { alert(error.response?.data?.detail || 'Upload failed'); } finally { setUploadingImage(false); }
    }
    e.target.value = '';
  };

  const handleRemoveImage = async (imageId) => {
    try { await axios.delete(`${API_URL}/api/community/images/${imageId}`, { withCredentials: true }); setPendingImages(prev => prev.filter(img => img.image_id !== imageId)); } catch {}
  };

  const handleCreatePost = async () => {
    if (!newPost.body.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/community/posts`, {
        ...newPost, media_urls: pendingImages.map(img => img.image_id), feed_type: 'organization'
      }, { withCredentials: true });
      setNewPost({ title: '', body: '', post_type: 'general_update' });
      setPendingImages([]);
      setShowComposer(false);
      const roleKey = user?.role === 'parent' ? 'parent' : (user?.role === 'coach' || user?.role === 'team_staff') ? 'coach' : 'parent';
      const needsApproval = !isAdmin && (communityPermissions?.require_approval?.[roleKey] ?? (user?.role === 'parent'));
      if (needsApproval) alert('Your post has been submitted for review.');
      fetchFeed();
    } catch (error) { alert(error.response?.data?.detail || 'Failed to create post'); } finally { setSubmitting(false); }
  };

  const handleReact = async (postId, reactionType) => {
    try {
      const post = posts.find(p => p.post_id === postId);
      if (post?.user_reaction === reactionType) {
        await axios.delete(`${API_URL}/api/community/posts/${postId}/react`, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/community/posts/${postId}/react`, { post_id: postId, reaction_type: reactionType }, { withCredentials: true });
      }
      setShowReactionPicker(null);
      fetchFeed();
    } catch {}
  };

  const handleComment = async (postId, commentText) => {
    const text = commentText || newComment;
    if (!text.trim()) return;
    try {
      await axios.post(`${API_URL}/api/community/posts/${postId}/comments`, { post_id: postId, content: text }, { withCredentials: true });
      setInlineComments(prev => ({ ...prev, [postId]: '' }));
      setNewComment('');
      if (selectedPost?.post_id === postId) {
        const res = await axios.get(`${API_URL}/api/community/posts/${postId}`, { withCredentials: true });
        setSelectedPost(res.data);
      }
      fetchFeed();
    } catch {}
  };

  const handleViewPost = async (postId) => {
    try { const res = await axios.get(`${API_URL}/api/community/posts/${postId}`, { withCredentials: true }); setSelectedPost(res.data); } catch {}
  };

  const handlePinPost = async (postId) => {
    try { await axios.post(`${API_URL}/api/community/posts/${postId}/pin`, {}, { withCredentials: true }); fetchFeed(); } catch {}
  };

  const scrollDivisions = (dir) => {
    if (divScrollRef.current) divScrollRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleEmojiSelect = (emoji, target) => {
    if (target === 'composer') {
      setNewPost(prev => ({ ...prev, body: prev.body + emoji.native }));
    } else {
      // target is a post_id for inline comment
      setInlineComments(prev => ({ ...prev, [target]: (prev[target] || '') + emoji.native }));
    }
    setShowEmojiPicker(null);
  };

  const EmojiPickerPopover = ({ target, position = 'above' }) => (
    <>
      <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(null)} />
      <div className={`absolute ${position === 'above' ? 'bottom-10' : 'top-10'} right-0 z-50 shadow-xl rounded-xl overflow-hidden`} data-testid={`emoji-picker-${target}`}>
        <Picker data={data} onEmojiSelect={(e) => handleEmojiSelect(e, target)} theme="light" previewPosition="none" skinTonePosition="search" perLine={8} maxFrequentRows={2} navPosition="bottom" />
      </div>
    </>
  );

  // ============ DIVISION TILES ============
  const DivisionTiles = () => (
    <div className="relative mb-6" data-testid="division-tiles">
      <button onClick={() => scrollDivisions(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center hover:bg-gray-50 border">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div ref={divScrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide px-10 py-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {/* All Posts tile */}
        <button
          onClick={() => setActiveDivision('all')}
          className="flex flex-col items-center flex-shrink-0 group"
          data-testid="division-tile-all"
        >
          <div className={`w-16 h-16 rounded-2xl overflow-hidden transition-all ${
            activeDivision === 'all' ? 'ring-[3px] ring-red p-0.5' : 'ring-2 ring-gray-200 p-0.5 group-hover:ring-gray-400'
          }`}>
            <div className="w-full h-full rounded-xl overflow-hidden bg-white flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_d8bf1b61-a9b7-4610-b686-61792bda2646/artifacts/ih9l03gi_image.png" alt="All" className="w-12 h-12 object-contain" />
            </div>
          </div>
          <span className={`text-[11px] mt-1.5 font-medium ${activeDivision === 'all' ? 'text-navy' : 'text-gray-500'}`}>All</span>
        </button>
        {divisions.map(div => {
          const isFollowing = followedDivisions.includes(div.division_id);
          const isLoadingFollow = followLoading[div.division_id];
          return (
            <div
              key={div.division_id}
              className="flex flex-col items-center flex-shrink-0 group relative"
            >
              <button
                onClick={() => setActiveDivision(div.division_id)}
                className="flex flex-col items-center"
                data-testid={`division-tile-${div.division_id}`}
              >
                <div className={`w-16 h-16 rounded-2xl overflow-hidden transition-all ${
                  activeDivision === div.division_id ? 'ring-[3px] ring-red p-0.5' : isFollowing ? 'ring-[3px] ring-green-400 p-0.5' : 'ring-2 ring-gray-200 p-0.5 group-hover:ring-gray-400'
                }`}>
                  <div className="w-full h-full rounded-xl overflow-hidden bg-white flex items-center justify-center">
                    {div.logo_url ? (
                      <img src={div.logo_url} alt={div.name} className="w-full h-full object-cover" />
                    ) : (
                      <Trophy className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                </div>
                <span className={`text-[11px] mt-1.5 font-medium max-w-[68px] truncate ${
                  activeDivision === div.division_id ? 'text-navy' : 'text-gray-500'
                }`}>{div.name.replace(' Chiefs', '').replace('Cheer ', '')}</span>
              </button>
              {/* Follow/Unfollow toggle */}
              <button
                onClick={(e) => { e.stopPropagation(); toggleFollow(div.division_id); }}
                disabled={isLoadingFollow}
                className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] shadow-sm border transition-all z-10 ${
                  isFollowing
                    ? 'bg-green-500 border-green-600 text-white hover:bg-red-400 hover:border-red-500'
                    : 'bg-white border-gray-300 text-gray-400 hover:bg-green-50 hover:border-green-400 hover:text-green-500'
                }`}
                data-testid={`follow-toggle-${div.division_id}`}
                title={isFollowing ? 'Unfollow' : 'Follow'}
              >
                {isLoadingFollow ? (
                  <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                ) : isFollowing ? (
                  <CheckCircle2 className="w-3 h-3" />
                ) : (
                  <Plus className="w-3 h-3" />
                )}
              </button>
            </div>
          );
        })}
      </div>
      <button onClick={() => scrollDivisions(1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center hover:bg-gray-50 border">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );

  // ============ POST CARD (Instagram-style) ============
  const PostCard = ({ post }) => {
    const timeAgo = formatDistanceToNow(new Date(post.created_at), { addSuffix: true });
    const hasMedia = post.resolved_media && post.resolved_media.length > 0;
    const isExpanded = expandedComments[post.post_id];
    const inlineComment = inlineComments[post.post_id] || '';
    const roleColor = ROLE_COLORS[post.author_role] || 'ring-gray-300';
    const roleLabel = ROLE_LABELS[post.author_role] || '';
    const totalReactions = Object.values(post.reaction_summary || {}).reduce((a, b) => a + b, 0);

    return (
      <div className="bg-white border border-gray-100 rounded-xl mb-4 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`post-card-${post.post_id}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ring-2 ${roleColor} flex items-center justify-center bg-navy text-white text-sm font-bold`}>
              {getInitials(post.author_name)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-gray-900">{post.author_name}</span>
                {roleLabel && (
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                    post.author_role?.includes('admin') ? 'bg-red/10 text-red' :
                    post.author_role === 'coach' || post.author_role === 'team_staff' ? 'bg-navy/10 text-navy' :
                    'bg-green-50 text-green-700'
                  }`}>{roleLabel}</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <span>{timeAgo}</span>
                {post.team_name && <><span>·</span><span className="text-gray-500">{post.team_name}</span></>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {post.is_pinned && <Pin className="w-4 h-4 text-gold fill-gold" />}
            {isAdmin && (
              <button onClick={() => handlePinPost(post.post_id)} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
                <MoreHorizontal className="w-5 h-5 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Media (Instagram-style - full width, no padding) */}
        {hasMedia && (
          <div className="relative">
            {post.resolved_media.length === 1 ? (
              <SingleMedia media={post.resolved_media[0]} postId={post.post_id} idx={0} />
            ) : (
              <MediaCarousel media={post.resolved_media} postId={post.post_id} />
            )}
          </div>
        )}

        {/* Action bar */}
        <div className="px-4 pt-3 pb-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              {/* Like/Heart button */}
              <button
                onClick={() => handleReact(post.post_id, 'like')}
                onDoubleClick={() => handleReact(post.post_id, 'like')}
                className="group"
                data-testid={`react-like-${post.post_id}`}
              >
                <Heart className={`w-6 h-6 transition-all ${
                  post.user_reaction === 'like' ? 'text-red-500 fill-red-500 scale-110' : 'text-gray-700 hover:text-gray-500 group-active:scale-90'
                }`} />
              </button>
              <button onClick={() => { setExpandedComments(prev => ({ ...prev, [post.post_id]: !prev[post.post_id] })); }} data-testid={`comments-btn-${post.post_id}`}>
                <MessageCircle className="w-6 h-6 text-gray-700 hover:text-gray-500 transition-colors" />
              </button>
              {/* Reaction picker toggle */}
              <div className="relative">
                <button onClick={() => setShowReactionPicker(showReactionPicker === post.post_id ? null : post.post_id)}>
                  <Share2 className="w-5 h-5 text-gray-700 hover:text-gray-500 transition-colors" />
                </button>
                {showReactionPicker === post.post_id && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowReactionPicker(null)} />
                    <div className="absolute bottom-8 left-0 bg-white rounded-full shadow-xl border px-2 py-1.5 flex gap-1 z-50" data-testid="reaction-picker">
                      {Object.entries(REACTION_ICONS).map(([type, { icon: Icon, color }]) => (
                        <button
                          key={type}
                          onClick={() => handleReact(post.post_id, type)}
                          className={`p-2 rounded-full hover:bg-gray-100 transition-all hover:scale-125 ${post.user_reaction === type ? 'bg-gray-100' : ''}`}
                          title={REACTION_ICONS[type].label}
                          data-testid={`react-${type}-${post.post_id}`}
                        >
                          <Icon className={`w-5 h-5 ${post.user_reaction === type ? color : 'text-gray-600'}`} />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            <button onClick={() => handlePinPost(post.post_id)} className={isAdmin ? '' : 'hidden'}>
              <Bookmark className={`w-6 h-6 ${post.is_pinned ? 'text-navy fill-navy' : 'text-gray-700 hover:text-gray-500'} transition-colors`} />
            </button>
          </div>

          {/* Reaction count */}
          {totalReactions > 0 && (
            <p className="text-sm font-semibold text-gray-900 mb-1">
              {totalReactions} {totalReactions === 1 ? 'like' : 'likes'}
            </p>
          )}

          {/* Post text */}
          <div className="mb-2">
            <p className="text-sm">
              <span className="font-semibold text-gray-900 mr-1.5">{post.author_name}</span>
              <span className="text-gray-700">{post.body?.length > 150 && !post._expanded ? post.body.slice(0, 150) + '...' : post.body}</span>
            </p>
            {post.title && <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wide font-medium">{post.title}</p>}
          </div>

          {/* View all comments link */}
          {(post.comment_count > 0) && !isExpanded && (
            <button
              onClick={() => { handleViewPost(post.post_id); setExpandedComments(prev => ({ ...prev, [post.post_id]: true })); }}
              className="text-sm text-gray-400 mb-1 hover:text-gray-600 transition-colors"
            >
              View {post.comment_count > 1 ? `all ${post.comment_count} comments` : '1 comment'}
            </button>
          )}

          {/* Inline comments */}
          {isExpanded && selectedPost?.post_id === post.post_id && selectedPost.comments?.length > 0 && (
            <div className="space-y-2 mb-2">
              {selectedPost.comments.slice(-3).map(c => (
                <p key={c.comment_id} className="text-sm">
                  <span className="font-semibold text-gray-900 mr-1.5">{c.author_name}</span>
                  <span className="text-gray-700">{c.content}</span>
                </p>
              ))}
              {selectedPost.comments.length > 3 && (
                <button onClick={() => handleViewPost(post.post_id)} className="text-xs text-gray-400 hover:text-gray-600">
                  View all {selectedPost.comments.length} comments
                </button>
              )}
            </div>
          )}

          {/* Timestamp */}
          <p className="text-[11px] text-gray-300 uppercase tracking-wide mb-2">
            {format(new Date(post.created_at), 'MMMM d, yyyy')}
          </p>
        </div>

        {/* Inline comment input */}
        <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-50 relative">
          <div className="w-7 h-7 rounded-full bg-navy/10 flex items-center justify-center text-xs font-bold text-navy">
            {getInitials(user?.name)}
          </div>
          <input
            type="text"
            value={inlineComment}
            onChange={(e) => setInlineComments(prev => ({ ...prev, [post.post_id]: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && handleComment(post.post_id, inlineComment)}
            placeholder="Add a comment..."
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-gray-300"
            data-testid={`inline-comment-${post.post_id}`}
          />
          <div className="relative">
            <button onClick={() => setShowEmojiPicker(showEmojiPicker === post.post_id ? null : post.post_id)} className="p-1 hover:bg-gray-100 rounded-full transition-colors" data-testid={`emoji-btn-${post.post_id}`}>
              <Smile className="w-5 h-5 text-gray-300 hover:text-gray-500" />
            </button>
            {showEmojiPicker === post.post_id && <EmojiPickerPopover target={post.post_id} position="above" />}
          </div>
          {inlineComment.trim() && (
            <button
              onClick={() => handleComment(post.post_id, inlineComment)}
              className="text-sm font-semibold text-blue-500 hover:text-blue-700 transition-colors"
              data-testid={`inline-comment-send-${post.post_id}`}
            >
              Post
            </button>
          )}
        </div>
      </div>
    );
  };

  // ============ MEDIA CAROUSEL (swipe left/right) ============
  const MediaCarousel = ({ media, postId }) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const carouselRef = useRef(null);
    const touchStart = useRef(null);

    const goTo = (idx) => {
      const clamped = Math.max(0, Math.min(idx, media.length - 1));
      setActiveSlide(clamped);
      if (carouselRef.current) {
        const slideWidth = carouselRef.current.offsetWidth;
        carouselRef.current.scrollTo({ left: clamped * slideWidth, behavior: 'smooth' });
      }
    };

    const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
      if (touchStart.current === null) return;
      const diff = touchStart.current - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) goTo(diff > 0 ? activeSlide + 1 : activeSlide - 1);
      touchStart.current = null;
    };

    const handleScroll = () => {
      if (carouselRef.current) {
        const slideWidth = carouselRef.current.offsetWidth;
        const newIdx = Math.round(carouselRef.current.scrollLeft / slideWidth);
        if (newIdx !== activeSlide) setActiveSlide(newIdx);
      }
    };

    return (
      <div className="relative" data-testid={`media-carousel-${postId}`}>
        {/* Slides */}
        <div
          ref={carouselRef}
          className="flex overflow-x-auto snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onScroll={handleScroll}
        >
          {media.map((m, idx) => (
            <div key={idx} className="w-full flex-shrink-0 snap-center">
              <SingleMedia media={m} postId={postId} idx={idx} />
            </div>
          ))}
        </div>

        {/* Left/Right arrows */}
        {activeSlide > 0 && (
          <button
            onClick={() => goTo(activeSlide - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-colors z-10"
            data-testid={`carousel-prev-${postId}`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {activeSlide < media.length - 1 && (
          <button
            onClick={() => goTo(activeSlide + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-colors z-10"
            data-testid={`carousel-next-${postId}`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {/* Slide count badge */}
        <div className="absolute top-3 right-3 bg-black/50 text-white text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm z-10">
          {activeSlide + 1}/{media.length}
        </div>

        {/* Dots */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {media.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              className={`rounded-full transition-all ${
                idx === activeSlide ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>
    );
  };

  // ============ SINGLE MEDIA RENDERER ============
  const SingleMedia = ({ media, postId, idx, compact }) => {
    const videoKey = `${postId}-${idx}`;
    const isPlaying = playingVideos[videoKey];
    const height = compact ? 'h-48' : 'h-80 sm:h-96';

    if (media.media_type === 'video') {
      return isPlaying ? (
        <video controls autoPlay preload="metadata" className={`w-full ${height} object-cover bg-black`} data-testid={`post-video-${postId}-${idx}`}>
          <source src={media.url} />
        </video>
      ) : (
        <div className={`relative cursor-pointer group ${height}`} onClick={() => setPlayingVideos(prev => ({ ...prev, [videoKey]: true }))} data-testid={`post-video-thumb-${postId}-${idx}`}>
          {media.thumbnail_url ? (
            <img src={media.thumbnail_url} alt="" className={`w-full ${height} object-cover`} />
          ) : (
            <div className={`w-full ${height} bg-gray-900 flex items-center justify-center`}><Video className="w-12 h-12 text-gray-600" /></div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Play className="w-8 h-8 text-navy ml-1" />
            </div>
          </div>
        </div>
      );
    }
    return (
      <img
        src={media.url}
        alt=""
        className={`w-full ${height} object-cover cursor-pointer`}
        onClick={() => setLightboxImage(media.url)}
        data-testid={`post-image-${postId}-${idx}`}
      />
    );
  };

  // ============ POST DETAIL MODAL ============
  const PostDetailModal = () => {
    if (!selectedPost) return null;
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPost(null)}>
        <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full ring-2 ${ROLE_COLORS[selectedPost.author_role] || 'ring-gray-300'} flex items-center justify-center bg-navy text-white text-xs font-bold`}>
                {getInitials(selectedPost.author_name)}
              </div>
              <div>
                <span className="font-semibold text-sm">{selectedPost.author_name}</span>
                <p className="text-[11px] text-gray-400">{format(new Date(selectedPost.created_at), 'MMM d, yyyy · h:mm a')}</p>
              </div>
            </div>
            <button onClick={() => setSelectedPost(null)} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-gray-500" /></button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {/* Media */}
            {selectedPost.resolved_media?.length > 0 && (
              <div>{selectedPost.resolved_media.map((media, idx) => (
                <SingleMedia key={idx} media={media} postId={selectedPost.post_id} idx={idx} />
              ))}</div>
            )}

            {/* Text */}
            <div className="px-4 py-3">
              {selectedPost.title && <h3 className="font-bold text-gray-900 mb-1">{selectedPost.title}</h3>}
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedPost.body}</p>
            </div>

            {/* Comments */}
            <div className="px-4 pb-3 space-y-3">
              {selectedPost.comments?.map(c => (
                <div key={c.comment_id} className="flex gap-2">
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0 mt-0.5">
                    {getInitials(c.author_name)}
                  </div>
                  <div>
                    <p className="text-sm"><span className="font-semibold mr-1">{c.author_name}</span>{c.content}</p>
                    <p className="text-[11px] text-gray-400">{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</p>
                  </div>
                </div>
              ))}
              {(!selectedPost.comments || selectedPost.comments.length === 0) && (
                <p className="text-sm text-gray-400 text-center py-4">No comments yet</p>
              )}
            </div>
          </div>

          {/* Comment input */}
          {selectedPost.comments_enabled !== false && (
            <div className="flex items-center gap-3 px-4 py-3 border-t relative">
              <div className="relative">
                <button onClick={() => setShowEmojiPicker(showEmojiPicker === 'modal' ? null : 'modal')} className="p-1 hover:bg-gray-100 rounded-full transition-colors" data-testid="modal-emoji-btn">
                  <Smile className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                </button>
                {showEmojiPicker === 'modal' && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(null)} />
                    <div className="absolute bottom-10 left-0 z-50 shadow-xl rounded-xl overflow-hidden">
                      <Picker data={data} onEmojiSelect={(e) => { setNewComment(prev => prev + e.native); setShowEmojiPicker(null); }} theme="light" previewPosition="none" skinTonePosition="search" perLine={8} maxFrequentRows={2} navPosition="bottom" />
                    </div>
                  </>
                )}
              </div>
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleComment(selectedPost.post_id)}
                placeholder="Add a comment..."
                className="flex-1 text-sm bg-transparent outline-none placeholder:text-gray-400"
                data-testid="comment-input"
              />
              <button
                onClick={() => handleComment(selectedPost.post_id)}
                disabled={!newComment.trim()}
                className="text-sm font-semibold text-blue-500 hover:text-blue-700 disabled:text-gray-300 transition-colors"
                data-testid="submit-comment-btn"
              >
                Post
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============ MAIN RENDER ============
  if (loading) {
    return (
      <div className="max-w-lg mx-auto space-y-4 pt-4">
        <div className="flex gap-3 overflow-hidden">{[...Array(7)].map((_, i) => <div key={i} className="w-16 h-16 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />)}</div>
        {[1, 2].map(i => <div key={i} className="bg-gray-200 rounded-xl h-96 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto" data-testid="community-content">
      {/* Division tiles */}
      <DivisionTiles />

      {/* Create post */}
      {!showComposer ? (
        <div className="bg-white border border-gray-100 rounded-xl mb-4 shadow-sm overflow-hidden">
          <button onClick={() => setShowComposer(true)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" data-testid="create-post-trigger">
            <div className={`w-10 h-10 rounded-full ring-2 ${ROLE_COLORS[user?.role] || 'ring-gray-300'} flex items-center justify-center bg-navy text-white text-sm font-bold`}>
              {getInitials(user?.name)}
            </div>
            <span className="text-sm text-gray-400">Share an update...</span>
            <div className="ml-auto flex gap-2">
              <Camera className="w-5 h-5 text-gray-300" />
            </div>
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl mb-4 shadow-sm overflow-hidden p-4">
          <div className="space-y-3">
            {!isAdmin && (() => {
              const roleKey = user?.role === 'parent' ? 'parent' : (user?.role === 'coach' || user?.role === 'team_staff') ? 'coach' : 'volunteer';
              const needsApproval = communityPermissions?.require_approval?.[roleKey] ?? (user?.role === 'parent');
              return needsApproval ? (
                <div className="bg-gold/10 border border-gold/30 rounded-lg p-3 flex items-start gap-2">
                  <Clock className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-600">Your post will be reviewed before publishing.</p>
                </div>
              ) : null;
            })()}
            <Input value={newPost.title} onChange={(e) => setNewPost({ ...newPost, title: e.target.value })} placeholder="Title (optional)" className="text-sm" data-testid="post-title-input" />
            <div className="relative">
              <Textarea value={newPost.body} onChange={(e) => setNewPost({ ...newPost, body: e.target.value })} placeholder="What's happening with the Chiefs?" rows={3} className="text-sm pr-10" data-testid="post-body-input" ref={composerRef} />
              <div className="absolute bottom-2 right-2">
                <button onClick={() => setShowEmojiPicker(showEmojiPicker === 'composer' ? null : 'composer')} className="p-1 hover:bg-gray-100 rounded-full transition-colors" data-testid="composer-emoji-btn">
                  <Smile className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                </button>
                {showEmojiPicker === 'composer' && <EmojiPickerPopover target="composer" position="above" />}
              </div>
            </div>

            {/* Pending media */}
            {pendingImages.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {pendingImages.map(img => (
                  <div key={img.image_id} className="relative group w-20 h-20">
                    {img.media_type === 'video' ? (
                      <div className="w-full h-full rounded-lg bg-gray-900 flex items-center justify-center relative overflow-hidden">
                        {img.thumbnailPreview ? <img src={img.thumbnailPreview} alt="" className="w-full h-full object-cover opacity-70" /> : <Video className="w-5 h-5 text-gray-400" />}
                        <Play className="absolute w-6 h-6 text-white drop-shadow-lg" />
                      </div>
                    ) : (
                      <img src={img.url} alt="" className="w-full h-full object-cover rounded-lg" />
                    )}
                    <button onClick={() => handleRemoveImage(img.image_id)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 shadow text-xs" data-testid={`remove-image-${img.image_id}`}><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {pendingImages.length < 3 && (
                  <label className="w-20 h-20 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-navy transition-colors">
                    <Plus className="w-4 h-4 text-gray-300" />
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm" multiple className="hidden" onChange={handleImageSelect} data-testid="image-upload-more" />
                  </label>
                )}
              </div>
            )}
            {uploadingImage && <div className="flex items-center gap-2 text-xs text-gray-400"><div className="w-3 h-3 border-2 border-navy border-t-transparent rounded-full animate-spin" />Uploading...</div>}

            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-2">
                <select value={newPost.post_type} onChange={(e) => setNewPost({ ...newPost, post_type: e.target.value })} className="border rounded-lg px-2 py-1.5 text-xs bg-gray-50" data-testid="post-type-select">
                  <option value="general_update">Update</option>
                  <option value="text_post">Text</option>
                  <option value="celebration">Celebration</option>
                  {(isAdmin || isCoach) && <option value="event_recap">Event Recap</option>}
                  {(isAdmin || isCoach) && <option value="player_spotlight">Spotlight</option>}
                </select>
                {pendingImages.length === 0 && (
                  <label className="flex items-center gap-1 px-2 py-1.5 border rounded-lg text-xs text-gray-500 hover:bg-gray-50 cursor-pointer" data-testid="image-upload-trigger">
                    <Camera className="w-3.5 h-3.5" /><span>Media</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm" multiple className="hidden" onChange={handleImageSelect} data-testid="image-upload-input" />
                  </label>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setShowComposer(false); setNewPost({ title: '', body: '', post_type: 'general_update' }); setPendingImages([]); }} className="text-xs">Cancel</Button>
                <Button size="sm" onClick={handleCreatePost} disabled={!newPost.body.trim() || submitting} className="bg-red hover:bg-red/90 text-white text-xs font-bold" data-testid="submit-post-btn">
                  {submitting ? '...' : 'Post'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feed */}
      {posts.length > 0 ? (
        <div>
          {(embedded ? posts.slice(0, 5) : posts).map(post => <PostCard key={post.post_id} post={post} />)}
          {embedded && posts.length > 5 && (
            <button
              onClick={() => window.location.href = '/community'}
              className="w-full py-3 text-sm font-semibold text-navy hover:text-red transition-colors text-center"
              data-testid="view-more-posts-btn"
            >
              View {posts.length - 5} more posts in Community
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl p-12 text-center shadow-sm">
          <div className="w-16 h-16 mx-auto mb-4 bg-navy/5 rounded-full flex items-center justify-center">
            <Camera className="w-8 h-8 text-navy/20" />
          </div>
          <h3 className="font-display text-lg font-bold text-navy mb-1">
            {activeDivision !== 'all' ? 'No posts in this division yet' : 'No posts yet'}
          </h3>
          <p className="text-sm text-gray-400 max-w-xs mx-auto">Share photos, updates, and highlights with the Chiefs community.</p>
        </div>
      )}

      {/* Post detail modal */}
      <PostDetailModal />

      {/* Parent FAQ - only show on full community page */}
      {!embedded && <ParentFAQ autoOpen={faqAutoOpen} />}

      {/* Image Lightbox */}
      {lightboxImage && (
        <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightboxImage(null)} data-testid="image-lightbox">
          <button className="absolute top-4 right-4 text-white/70 hover:text-white z-10" onClick={() => setLightboxImage(null)}><X className="w-8 h-8" /></button>
          <img src={lightboxImage} alt="" className="max-w-full max-h-[90vh] object-contain rounded" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default CommunityContent;
